<template>
  <aside
    :class="{ 'project-sidebar': true, 'is-open': isOpen }"
    v-bind="$attrs"
    v-on="$listeners"
    @click="close"
  >
    <button class="button-open" @click.stop="open">
      <SloIcon name="Pencil" />
      <span>{{ 'Edit' | t }}</span>
    </button>
    <header @click.stop>
      <button class="button-close" @click="close">
        <SloIcon name="XMark" />
      </button>
      <div class="buttons-wrapper">
        <slo-button
          class="button-preview"
          v-if="authenticated && hasProducts"
          :href="'/designspec/api/generate/' + project.id"
          target="_blank"
          fill="outline"
        >
          <span>{{ 'Preview' | t }}</span>
        </slo-button>
        <router-link
          :is="!hasProducts ? 'span' : 'router-link'"
          to="share"
          append
        >
          <slo-button fill="outline" :disabled="!hasProducts">
            <span>{{ 'Share' | t }}</span>
          </slo-button>
        </router-link>
        <router-link
          :is="!hasProducts ? 'span' : 'router-link'"
          to="download"
          append
        >
          <slo-button fill="solid" :disabled="!hasProducts">
            <SloIcon name="ArrowDownToLine" />
            <span>{{ 'Download DesignSpec' | t }}</span>
          </slo-button>
        </router-link>
      </div>
    </header>
    <main v-stack-y:md @click.stop>
      <cover-image />
      <section v-stack-y:sm>
        <header>
          <h2>{{ 'Project Details' | t }}</h2>
          <slo-link to="edit" append>
            <SloIcon name="Pencil" />
            <span>{{ 'Edit' | t }}</span>
          </slo-link>
          <p>{{ 'Update project cover image and keep notes here.' | t }}</p>
        </header>
        <div class="body" v-if="details" v-html="details" />
      </section>
    </main>
  </aside>
</template>

<script>
import {mapGetters} from 'vuex';
import t from '../../main/i18n';
import CoverImage from './CoverImage.vue';
import SloLink from '../../../lib/components/SloLink.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloIcon from '../../../lib/components/SloIcon.vue';

export default {
  name: 'ProjectSidebar',
  components: {
    CoverImage,
    SloLink,
    SloButton,
    SloIcon
  },
  data() {
    return {
      isOpen: false
    };
  },
  filters: {
    t
  },
  watch: {
    isOpen(isOpen) {
      if (isOpen) {
        document.body.style.setProperty('overflow', 'hidden');
      } else {
        document.body.style.removeProperty('overflow');
      }
    }
  },
  computed: {
    ...mapGetters('auth', ['authenticated']),
    ...mapGetters('designspec', {getProject: 'project'}),
    project() {
      return this.getProject(this.$route.params.id);
    },
    details() {
      const {notes, company, city, state} = this.project;
      if (!(notes || company || city || state)) return;
      const location = [city, state].filter(v => v).join(', ');
      const intro = [company, location].filter(v => v).join(' • ');
      return [intro, notes]
        .map(value => value && `<p>${value}</p>`)
        .filter(v => v)
        .join('');
    },
    hasProducts() {
      return Boolean(this.project.sections);
    }
  },
  methods: {
    open() {
      this.isOpen = true;
    },
    close() {
      this.isOpen = false;
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../../scss/variables';
@import '../../../scss/system';

.button-open {
  position: fixed;
  display: flex;
  align-items: center;
  justify-content: center;
  right: 0;
  height: 48px;
  background: white;
  color: $color-blue-400;
  border-radius: 0;
  border-top-left-radius: 24px;
  border-bottom-left-radius: 24px;
  padding: 0;
  margin: 0;
  margin-top: 48px;
  padding-right: 16px;
  box-shadow: $shadow-lg !important;
  overflow: visible;
  @include text('sm', 'strong', false);

  .slo-icon {
    margin-left: 16px;
    margin-right: 8px;
  }

  span {
    display: inline-block;
  }
}

.button-close {
  height: 48px;
  width: 100%;
  padding: 0;
  margin-top: auto;
  box-shadow: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: flex-start;

  .slo-icon {
    font-size: 24px;
  }
}

.slo-button {
  height: 48px;
}

.project-sidebar {
  position: fixed;
  display: flex;
  flex-flow: column;
  visibility: visible;
  pointer-events: initial;
  right: -100%;
  z-index: 999;
  transition: transform 200ms cubic-bezier(0.23, 1, 0.32, 1);
  max-width: calc(100vw - 32px);

  @media screen and (min-width: $screen-sm-min) {
    max-width: 50vw;
  }

  &::before {
    content: '';
    position: fixed;
    top: -50%;
    left: -50%;
    height: 300vh;
    width: 300vw;
    background: rgba(222, 222, 222, 0.8);
    transform: translate(-50%, -50%);
    z-index: -1;
    pointer-events: none;
    opacity: 0;
    transition: opacity 150ms cubic-bezier(0.23, 1, 0.32, 1);
  }

  &::after {
    content: '';
    position: absolute;
    top: -128px;
    left: -16px;
    right: -16px;
    height: 100vh;
    background: white;
    z-index: -1;
    box-shadow: $shadow-xl;
    display: none;
  }

  &.is-open {
    transform: translateX(calc(-100vw - 16px));

    &::before,
    &::after {
      pointer-events: inherit;
      display: block;
      opacity: 1;
    }

    .button-open {
      display: none !important;
    }
  }

  main {
    background: none;
    margin: 0 auto;
    margin-top: 22px;
  }

  @media screen and (min-width: 1280px) {
    position: relative;
    margin-left: 32px;
    flex-basis: 33%;
    right: 0;
    visibility: visible;
    pointer-events: initial;

    &.is-open {
      transform: none;

      &::before,
      &::after {
        pointer-events: none;
        display: none;
        opacity: 0;
      }

      .button-open {
        display: none !important;
      }
    }

    .button-close,
    .button-open {
      display: none !important;
    }
  }

  > header {
    display: flex;
    flex-flow: row wrap;
    justify-content: center;
    padding: 16px 0 8px;
    width: 100%;
    height: auto;

    .buttons-wrapper {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      width: 100%;
    }

    .slo-button {
      display: block;
      margin-right: 8px;
      margin-left: 8px;
      margin-top: 8px;
      max-width: max-content;
      text-align: center;

      .msie & {
        max-width: 25%;

        &:last-child {
          display: flex;
          max-width: 198px;
        }
      }

      @media screen and (min-width: $screen-sm-min) {
        flex-basis: 100%;
      }
    }
  }

  section {
    padding-top: 16px;
    border-top: 1px solid $color-gray-400;
    display: flex;
    flex-direction: column;
  }

  section > header {
    display: flex;
    flex-flow: row wrap;

    h2 {
      @include text('lg', 'strong');
      flex: 1 0 auto;
      width: 80%;
    }

    p {
      @include text('sm');
      color: $color-gray-700;
      margin-top: 8px;
      flex: 1 0 auto;
    }
  }
}

.msie .project-sidebar.is-open {
  transform: translateX(-102vw);
}
</style>
